import { FC } from 'react';
import { NoticeBannerModal } from '~/pages/AdminSettingsPage/components';
import { NoticeBannerModalProps } from '~/pages/AdminSettingsPage/components/NoticeBannerModal';
import { PublishToAtlanModal } from '~/pages/DataCatalogPage/components';
import { PublishToAtlanModalProps } from '~/pages/DataCatalogPage/components/PublishToAtlanModal';
import { DatasetModal } from '~/pages/DatasetDetailsPage/components/DatasetModal';
import { ImportDataModal } from '~/pages/DatasetDetailsPage/components/ImportDataModal';
import { ImportDataModalProps } from '~/pages/DatasetDetailsPage/components/ImportDataModal/ImportDataModal';
import RequestDatasetAccessModal, {
  RequestDatasetAccessModalProps,
} from '~/pages/DatasetDetailsPage/components/RequestDatasetAccessModal';
import { DeactivateProjectModal, ProjectEditPanel } from '~/pages/ProjectDetailsPage/components';
import { FailedDetailsModal, ScheduleJobModal } from '~/pages/ProjectDetailsPage/tabs/ETLJobsTab/components';
import { FailedDetailsModalProps } from '~/pages/ProjectDetailsPage/tabs/ETLJobsTab/components/FailedDetailsModal';
import LogsModal, { LogsModalProps } from '~/pages/ProjectDetailsPage/tabs/ETLJobsTab/components/LogsModal';
import { ScheduleJobModalProps } from '~/pages/ProjectDetailsPage/tabs/ETLJobsTab/components/ScheduleJobModal';
import {
  CreateEmrClusterModal,
  CreateEmrStudioModal,
} from '~/pages/ProjectDetailsPage/tabs/StudioIntegrationTab/components';
import { CreateEmrClusterModalProps } from '~/pages/ProjectDetailsPage/tabs/StudioIntegrationTab/components/CreateEmrClusterModal';
import { CreateWorkbenchModal } from '~/pages/ProjectDetailsPage/tabs/WorkbenchTab/components';
import { useAppSelector } from '~/redux';
import { ModalTypesEnum } from '~/redux/slices/modalSlice';
import {
  ChangeRegionModal,
  ConfirmModal,
  ConnectionInstructions,
  DatasetConnectionInstructions,
  DeleteConfirmationModal,
  FileSystemActionModal,
} from '.';
import { ConfirmModalProps } from './ConfirmModal';
import { ConnectionInstructionsProps } from './ConnectionInstructions/ConnectionInstructions';
import { DatasetConnectionInstructionsProps } from './DatasetConnectionInstructions';
import { DeleteConfirmationModalProps } from './DeleteConfirmationModal/DeleteConfirmationModal';
import { FileSystemActionModalProps } from './FileSystemActionModal';
import { ChangeRegionModalProps } from './RegionSelect/ChangeRegionModal';

const RootModal: FC = () => {
  const modals = useAppSelector((state) => state.modal);

  if (!modals.length) return null;

  return modals.map((modal) => {
    switch (modal.type) {
      case ModalTypesEnum.DELETE_CONFIRMATION:
        return (
          <DeleteConfirmationModal
            key={modal.id}
            payload={{ id: modal.id, ...modal.payload } as DeleteConfirmationModalProps['payload']}
          />
        );
      case ModalTypesEnum.CONFIRM:
        return (
          <ConfirmModal key={modal.id} payload={{ id: modal.id, ...modal.payload } as ConfirmModalProps['payload']} />
        );
      case ModalTypesEnum.FILE_SYSTEM_ACTION:
        return (
          <FileSystemActionModal
            key={modal.id}
            payload={{ id: modal.id, ...modal.payload } as FileSystemActionModalProps['payload']}
          />
        );
      case ModalTypesEnum.CREATE_WORKBENCH:
        return <CreateWorkbenchModal key={modal.id} id={modal.id} />;
      case ModalTypesEnum.CREATE_EMR_STUDIO:
        return <CreateEmrStudioModal key={modal.id} id={modal.id} />;
      case ModalTypesEnum.CREATE_EMR_CLUSTER:
        return (
          <CreateEmrClusterModal
            key={modal.id}
            payload={{ id: modal.id, ...modal.payload } as CreateEmrClusterModalProps['payload']}
          />
        );
      case ModalTypesEnum.CREATE_PROJECT:
      case ModalTypesEnum.EDIT_PROJECT:
        return <ProjectEditPanel key={modal.id} id={modal.id} />;
      case ModalTypesEnum.CONNECTION_INSTRUCTIONS:
        return (
          <ConnectionInstructions
            key={modal.id}
            payload={{ id: modal.id, ...modal.payload } as ConnectionInstructionsProps['payload']}
          />
        );
      case ModalTypesEnum.DATASET_CONNECTION_INSTRUCTIONS:
        return (
          <DatasetConnectionInstructions
            key={modal.id}
            payload={{ id: modal.id, ...modal.payload } as DatasetConnectionInstructionsProps['payload']}
          />
        );
      case ModalTypesEnum.REQUEST_DATASET_ACCESS:
        return (
          <RequestDatasetAccessModal
            key={modal.id}
            payload={{ id: modal.id, ...modal.payload } as RequestDatasetAccessModalProps['payload']}
          />
        );
      case ModalTypesEnum.CREATE_DATASET:
      case ModalTypesEnum.EDIT_DATASET:
        return <DatasetModal key={modal.id} id={modal.id} />;
      case ModalTypesEnum.IMPORT_DATA_TO_DATASET:
        return (
          <ImportDataModal
            key={modal.id}
            payload={{ id: modal.id, ...modal.payload } as ImportDataModalProps['payload']}
          />
        );
      case ModalTypesEnum.DEACTIVATE_PROJECT:
        return <DeactivateProjectModal key={modal.id} id={modal.id} />;
      case ModalTypesEnum.ETL_SCHEDULE_JOB:
        return (
          <ScheduleJobModal
            key={modal.id}
            payload={{ id: modal.id, ...modal.payload } as ScheduleJobModalProps['payload']}
          />
        );
      case ModalTypesEnum.FAILED_ETL_JOB_DETAILS:
        return (
          <FailedDetailsModal
            key={modal.id}
            payload={{ id: modal.id, ...modal.payload } as FailedDetailsModalProps['payload']}
          />
        );
      case ModalTypesEnum.JOB_LOGS:
        return <LogsModal key={modal.id} payload={{ id: modal.id, ...modal.payload } as LogsModalProps['payload']} />;
      case ModalTypesEnum.CHANGE_REGION:
        return (
          <ChangeRegionModal
            key={modal.id}
            payload={{ id: modal.id, ...modal.payload } as ChangeRegionModalProps['payload']}
          />
        );
      case ModalTypesEnum.PUBLISH_TO_ATLAN:
        return (
          <PublishToAtlanModal
            key={modal.id}
            payload={{ id: modal.id, ...modal.payload } as PublishToAtlanModalProps['payload']}
          />
        );
      case ModalTypesEnum.NOTICE_BANNER:
        return (
          <NoticeBannerModal
            key={modal.id}
            payload={{ id: modal.id, ...modal.payload } as NoticeBannerModalProps['payload']}
          />
        );
      default:
        return null;
    }
  });
};

export default RootModal;
